import { IFundingRepository, IFundingContractRepository } from '../domain/repositories';
import { FundingContractEntity } from '../domain/entities';
import { FundingExistOptions, SupportProps } from './dto';
import { AllMpsPoolType, AllPoolType } from '@services/MarppleShop/shared/database/db';
import { ItemOrderId } from '@services/MarppleShop/features/Product/domain';
import {
  IProductMetaQueryRepository,
  ProductId,
  ProductMetaQueryRepositoryFactory,
} from '@services/MarppleShop/features/ProductMeta';
import { Money } from '../../../../../shared/features/domain/value-objects/Money.ts';
import {
  NotExistFundingContractError,
  NotFundingError,
} from '@services/MarppleShop/features/Funding/error/funding.ts';
import { FundingContractRepository, FundingRepository } from '@services/MarppleShop/features/Funding';
import { FundingDetailResponse } from '@services/MarppleShop/features/Funding/application/dto/funding.dto.ts';
import { head, last, map, pipe, toArray } from '@fxts/core';

export class FundingService {
  constructor(
    private readonly fundingRepository: IFundingRepository,
    private readonly contractRepository: IFundingContractRepository,
    // TODO: fundingRepository에서 fundingMeta만 가져올 수 있도록 수정 필요
    private readonly productMetaQueryRepository: IProductMetaQueryRepository,
  ) {}

  static create(db: AllPoolType, dbt: AllMpsPoolType): FundingService {
    return new FundingService(
      new FundingRepository(db, dbt),
      new FundingContractRepository(db),
      ProductMetaQueryRepositoryFactory.createMco(db),
    );
  }

  /**
   * 펀딩 후원
   */
  async support(props: SupportProps): Promise<void> {
    // 1. 펀딩 상품 정보 조회 및 유효성 검증 (ProductMeta의 책임)
    const productIdVO = new ProductId(props.productId);
    const productMeta = await this.productMetaQueryRepository.findByProductId(productIdVO);
    const fundingMetaVO = productMeta?.getFundingMeta();

    if (!(productMeta && fundingMetaVO)) {
      throw new NotFundingError();
    }

    // 2. 새 후원 계약 생성
    const contract = FundingContractEntity.create({
      productId: productMeta.getProductId(),
      itemOrderId: ItemOrderId.of(props.itemOrderId),
      userId: props.userId,
      money: Money.of(props.amount),
    });

    // 3. 계약 저장
    await this.contractRepository.save(contract);
  }

  /**
   * 펀딩 후원 취소
   */
  async cancel(itemOrderId: number): Promise<void> {
    // 1. 계약 조회
    const contract = await this.contractRepository.findByItemOrderId(ItemOrderId.of(itemOrderId));

    if (!contract) {
      throw new NotExistFundingContractError();
    }

    // 2. 계약 취소
    contract.cancel();

    // 3. 계약 저장
    await this.contractRepository.save(contract);
  }

  /**
   * TODO: 펀딩 상품 조회 구현
   * COMING_SOON: '펀딩 공개 예정',
   * ING: '펀딩 진행 중',
   * BREAKTHROUGH: '펀딩 진행 중 목표금액 돌파',
   * SUCCESS: '펀딩 성공',
   * FAILED: '펀딩 실패',
   */
  async getFunding(productId: number): Promise<FundingDetailResponse> {
    const fundingEntity = await this.fundingRepository.findByProductId(new ProductId(productId));

    if (!fundingEntity) throw new NotFundingError();

    const [totalAmounts, totalSupporterCounts, supporters] = await Promise.all([
      this.contractRepository.totalAmounts([fundingEntity.productId]),
      this.contractRepository.totalSupporterCounts([fundingEntity.productId]),
      this.contractRepository.findSupporterRankings(fundingEntity.productId, new Date(), Money.of(0), 15),
    ]);

    return {
      funding: {
        productId: fundingEntity.productId.value,
        description: fundingEntity.fundingMeta.getDescription(),
        goalAmount: fundingEntity.fundingMeta.getTargetAmount(),
        totalAmount: pipe(totalAmounts, head, (totalAmount) => totalAmount?.totalAmount.getAmount() || 0),
        supporterCount: pipe(
          totalSupporterCounts,
          head,
          (totalSupporterCount) => totalSupporterCount?.totalSupporterCount || 0,
        ),
        supporterList: {
          data: pipe(
            supporters,
            map((supporter) => supporter.toData()),
            toArray,
          ),
          next_cursor: pipe(supporters, last, (supporter): [Date, number | null] =>
            supporter ? [supporter.createdAt, supporter.money.getAmount()] : [new Date(), null],
          ),
          has_more: supporters.length === 15,
        },
      },
    };
  }

  exist(options: FundingExistOptions) {
    return this.fundingRepository.exist({
      ...options,
      productIds: options.productIds
        ? pipe(
            options.productIds,
            map((productId) => new ProductId(productId)),
            toArray,
          )
        : undefined,
    });
  }
}
